import React, { useState, useEffect } from 'react';
import { HistoryPesee } from '@/services/historyService';
import FieldSelector, { ExportField } from './FieldSelector';
import { getAvailableExportFields, getDefaultFieldsForExport } from '@/services/exportFieldsConfig';
import exportService, { ExportOptions } from '@/services/exportService';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  weighings: HistoryPesee[];
  exportType: 'pdf' | 'excel' | 'csv';
}

const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  onClose,
  weighings,
  exportType
}) => {
  const [availableFields, setAvailableFields] = useState<ExportField[]>([]);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [customTitle, setCustomTitle] = useState<string>('');
  const [customFilename, setCustomFilename] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string>('');

  // Initialiser les champs disponibles et sélectionnés
  useEffect(() => {
    if (isOpen) {
      const fields = getAvailableExportFields();
      setAvailableFields(fields);
      
      // Sélectionner les champs par défaut pour ce type d'export
      const defaultFields = getDefaultFieldsForExport(exportType);
      setSelectedFields(defaultFields);
      
      // Titre par défaut
      const defaultTitles = {
        pdf: 'Rapport des Pesées',
        excel: 'Liste des Pesées',
        csv: 'Export des Pesées'
      };
      setCustomTitle(defaultTitles[exportType]);
      
      // Nom de fichier par défaut
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
      setCustomFilename(`pesees_${timestamp}`);
      
      setExportError('');
    }
  }, [isOpen, exportType]);

  const handleExport = async () => {
    if (selectedFields.length === 0) {
      setExportError('Veuillez sélectionner au moins un champ à exporter.');
      return;
    }

    setIsExporting(true);
    setExportError('');

    try {
      const options: ExportOptions = {
        fields: availableFields,
        selectedFieldKeys: selectedFields,
        filename: `${customFilename}.${exportType === 'excel' ? 'xlsx' : exportType}`,
        title: customTitle
      };

      switch (exportType) {
        case 'pdf':
          await exportService.exportToPDF(weighings, options);
          break;
        case 'excel':
          await exportService.exportToExcel(weighings, options);
          break;
        case 'csv':
          await exportService.exportToCSV(weighings, options);
          break;
      }

      onClose();
    } catch (error: any) {
      console.error('Erreur lors de l\'export:', error);
      setExportError(error.message || 'Erreur lors de l\'export');
    } finally {
      setIsExporting(false);
    }
  };

  const getExportTypeLabel = () => {
    const labels = {
      pdf: 'PDF',
      excel: 'Excel',
      csv: 'CSV'
    };
    return labels[exportType];
  };

  const getExportTypeIcon = () => {
    const icons = {
      pdf: 'fas fa-file-pdf text-red-600',
      excel: 'fas fa-file-excel text-green-600',
      csv: 'fas fa-file-csv text-blue-600'
    };
    return icons[exportType];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <i className={`${getExportTypeIcon()} text-2xl`}></i>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Export en {getExportTypeLabel()}
              </h2>
              <p className="text-sm text-gray-600">
                {weighings.length} pesée{weighings.length > 1 ? 's' : ''} à exporter
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          <div className="space-y-6">
            {/* Configuration de l'export */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre du document
                </label>
                <input
                  type="text"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Titre du document"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom du fichier
                </label>
                <input
                  type="text"
                  value={customFilename}
                  onChange={(e) => setCustomFilename(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nom du fichier"
                />
              </div>
            </div>

            {/* Sélection des champs */}
            <FieldSelector
              weighings={weighings}
              selectedFields={selectedFields}
              onFieldsChange={setSelectedFields}
              availableFields={availableFields}
            />

            {/* Message d'erreur */}
            {exportError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                <div className="flex items-center">
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  <span>{exportError}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="text-sm text-gray-600">
            <span className="font-medium">{selectedFields.length}</span> champ{selectedFields.length > 1 ? 's' : ''} sélectionné{selectedFields.length > 1 ? 's' : ''}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              disabled={isExporting}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleExport}
              disabled={isExporting || selectedFields.length === 0}
              className={`px-6 py-2 text-white rounded-lg transition-colors flex items-center ${
                exportType === 'pdf' 
                  ? 'bg-red-600 hover:bg-red-700 disabled:bg-gray-400' 
                  : exportType === 'excel'
                  ? 'bg-green-600 hover:bg-green-700 disabled:bg-gray-400'
                  : 'bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400'
              }`}
            >
              {isExporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Export en cours...
                </>
              ) : (
                <>
                  <i className={`fas fa-download mr-2`}></i>
                  Exporter en {getExportTypeLabel()}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
